// src/components/MembershipPage.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import authService from '../services/auth';
// Google OAuth kütüphanesinden buton bileşenini çağırıyoruz
import { GoogleLogin } from '@react-oauth/google';
import api from '../services/api'; // Backend istekleri için projenizdeki genel API servisi

// UI bileşenleri
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';

interface MembershipPageProps {
    onLogin?: () => void;
}

export function MembershipPage(props: MembershipPageProps) {
    const navigate = useNavigate();
    const { onLogin } = props;

    // Giriş (login), Kayıt (register) veya Şifremi Unuttum (forgot) ekranı kontrolü
    const [viewMode, setViewMode] = useState<'login' | 'register' | 'forgot'>('login');

    // --- GİRİŞ STATE ---
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // --- ŞİFREMİ UNUTTUM STATE ---
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);

    // --- KAYIT STATE ---
    const [registerData, setRegisterData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        passwordConfirm: '',
        userType: 'alumni',
    });

    const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { id, value } = e.target;
        setRegisterData(prev => ({ ...prev, [id]: value }));
    };

    // --- GİRİŞ İŞLEMİ ---
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const response = await authService.login(loginEmail, loginPassword);
            
            const accessToken = response?.data?.access || response?.access;
            const refreshToken = response?.data?.refresh || response?.refresh;

            if (accessToken) {
                localStorage.setItem('access', accessToken);
                localStorage.setItem('refresh', refreshToken);
                
                toast.success('Giriş başarılı! Yönlendiriliyorsunuz.');

                if (onLogin) {
                    onLogin();
                }
                
                window.location.href = '/'; 
            } else {
                toast.error('Giriş yapıldı ama token alınamadı.');
            }
        } catch (error) {
            console.error(error);
            toast.error('Giriş başarısız oldu. E-posta veya şifrenizi kontrol edin.');
        }
    };

    // --- GOOGLE ILE GİRİŞ BAŞARILI OLUNCA TETİKLENEN FONKSİYON ---
    const handleGoogleSuccess = async (credentialResponse: any) => {
        try {
            const googleToken = credentialResponse.credential;

            const response = await api.post('/auth/google/', {
                token: googleToken
            });

            const accessToken = response?.data?.access || response?.access;
            const refreshToken = response?.data?.refresh || response?.refresh;
            const userData = response?.data?.user; // Backend'den gelen kullanıcı bilgisi

            if (accessToken) {
                localStorage.setItem('access', accessToken);
                localStorage.setItem('refresh', refreshToken);
                
                if (userData && userData.id) {
                    localStorage.setItem('userId', String(userData.id));
                }
                if (userData && typeof userData.is_staff !== 'undefined') {
                    localStorage.setItem('isStaff', userData.is_staff ? 'true' : 'false');
                }

                toast.success('Google ile başarıyla giriş yapıldı!');

                if (onLogin) {
                    onLogin();
                }

                // EĞER YENİ KULLDIYSA ONBOARDING EKRANINA (PROFİLE) YÖNLENDİR
                if (response?.data?.is_new_user) {
                    navigate('/profil', { state: { registeredJustNow: true } });
                } else {
                    window.location.href = '/';
                }
            } else {
                toast.error('Google girişi başarılı fakat token alınamadı.');
            }
        } catch (error: any) {
            console.error('Google Giriş Hatası:', error);
            toast.error('Google ile giriş yapılırken bir hata oluştu.');
        }
    };

    // --- KAYIT İŞLEMİ ---
    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (registerData.password !== registerData.passwordConfirm) {
            toast.error('Şifreler uyuşmuyor, lütfen tekrar kontrol edin.');
            return;
        }

        try {
            const apiData = {
                email: registerData.email,
                password: registerData.password,
                FirstName: registerData.firstName,
                LastName: registerData.lastName,
                user_type: registerData.userType,
            };

            await authService.register(apiData);

            const loginResponse = await authService.login(registerData.email, registerData.password);
            
            const accessToken = loginResponse?.data?.access || loginResponse?.access;
            const refreshToken = loginResponse?.data?.refresh || loginResponse?.refresh;

            if (accessToken) {
                localStorage.setItem('access', accessToken);
                localStorage.setItem('refresh', refreshToken);
                
                toast.success('Kayıt başarılı! Profilinize yönlendiriliyorsunuz.');

                if (onLogin) {
                    onLogin(); 
                }
                navigate('/profil', { state: { registeredJustNow: true } });
            } else {
                toast.warning('Kaydınız yapıldı ancak otomatik giriş başarısız. Lütfen manuel giriş yapın.');
                setViewMode('login');
            }

        } catch (error: any) {
            console.error(error.response?.data);
            
            // Backend'den gelen hata verilerini kontrol ediyoruz
            const errorData = error.response?.data;
            let errorMessage = 'Lütfen tüm zorunlu alanları kontrol edin.';

            // Eğer e-posta daha önceden kayıtlıysa Django bu alanı 'email' veya 'Email' olarak döner
            if (errorData?.email || errorData?.Email) {
                const emailErr = errorData.email?.[0] || errorData.Email?.[0];
                if (emailErr && (emailErr.includes('already exists') || emailErr.includes('zaten'))) {
                    errorMessage = 'Bu e-posta adresi ile zaten bir hesap kayıtlı. Lütfen giriş yapın veya şifrenizi unuttuysanız sıfırlayın.';
                } else {
                    errorMessage = emailErr;
                }
            } else if (errorData?.detail) {
                errorMessage = errorData.detail;
            }

            toast.error(`Kayıt başarısız: ${errorMessage}`);
        }
    };

    // --- ŞİFREMİ UNUTTUM MAİL GÖNDERME İŞLEMİ ---
    const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setForgotLoading(true);
        try {
            await api.post('/auth/password-reset/', { email: forgotEmail });
            toast.success('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.');
            setViewMode('login');
        } catch (error: any) {
            console.error(error);
            toast.error('E-posta gönderilirken bir hata oluştu. Adresi kontrol edin.');
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="flex justify-center items-center min-h-[80vh] px-4">
            {viewMode === 'login' && (
                /* GİRİŞ EKRANI */
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="text-2xl text-center">Üye Girişi</CardTitle>
                        <CardDescription className="text-center">Sisteme erişmek için bilgilerinizi girin.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="loginEmail">E-posta</Label>
                                <Input 
                                    id="loginEmail" 
                                    type="email" 
                                    placeholder="ornek@email.com" 
                                    value={loginEmail} 
                                    onChange={(e) => setLoginEmail(e.target.value)} 
                                    required 
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="loginPassword">Şifre</Label>
                                <Input 
                                    id="loginPassword" 
                                    type="password" 
                                    placeholder="••••••••" 
                                    value={loginPassword} 
                                    onChange={(e) => setLoginPassword(e.target.value)} 
                                    required 
                                />
                            </div>
                            <Button type="submit" className="w-full">Giriş Yap</Button>
                        </form>

                        {/* --- GOOGLE LOGIN BUTONU --- */}
                        <div className="relative my-2 flex items-center justify-center">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-200"></div>
                            </div>
                            <span className="relative bg-white px-2 text-xs text-muted-foreground uppercase">veya</span>
                        </div>

                        <div className="w-full flex justify-center">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => toast.error('Google Giriş işlemi başarısız oldu.')}
                                theme="outline"
                                size="large"
                                width="100%"
                            />
                        </div>

                        <div className="pt-2 text-center space-y-2">
                            <button 
                                type="button" 
                                onClick={() => setViewMode('register')} 
                                className="text-sm text-blue-600 hover:underline block w-full text-center font-medium"
                            >
                                Hesabın yoksa hesap oluştur
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setViewMode('forgot')}
                                className="text-xs text-muted-foreground hover:underline"
                            >
                                Şifremi Unuttum
                            </button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {viewMode === 'register' && (
                /* KAYIT EKRANI */
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="text-2xl text-center">Hesap Oluştur</CardTitle>
                        <CardDescription className="text-center">Aramıza katılmak için formu doldurun.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleRegisterSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="firstName">Ad </Label>
                                    <Input 
                                        id="firstName" 
                                        placeholder="Adınız" 
                                        required 
                                        value={registerData.firstName} 
                                        onChange={handleRegisterChange} 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lastName">Soyad </Label>
                                    <Input 
                                        id="lastName" 
                                        placeholder="Soyadınız" 
                                        required 
                                        value={registerData.lastName} 
                                        onChange={handleRegisterChange} 
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">E-posta </Label>
                                <Input 
                                    id="email" 
                                    type="email" 
                                    placeholder="ornek@email.com" 
                                    required 
                                    value={registerData.email} 
                                    onChange={handleRegisterChange} 
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="userType">Kullanıcı Tipi </Label>
                                <select
                                    id="userType"
                                    value={registerData.userType}
                                    onChange={handleRegisterChange}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                    <option value="alumni">Mezun</option>
                                    <option value="teacher">Öğretmen</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Şifre </Label>
                                <Input 
                                    id="password" 
                                    type="password" 
                                    placeholder="••••••••" 
                                    required 
                                    value={registerData.password} 
                                    onChange={handleRegisterChange} 
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="passwordConfirm">Şifre Tekrar </Label>
                                <Input 
                                    id="passwordConfirm" 
                                    type="password" 
                                    placeholder="••••••••" 
                                    required 
                                    value={registerData.passwordConfirm} 
                                    onChange={handleRegisterChange} 
                                />
                            </div>

                            <Button type="submit" className="w-full">Kayıt Ol</Button>

                            <div className="pt-2 text-center">
                                <button 
                                    type="button" 
                                    onClick={() => setViewMode('login')} 
                                    className="text-sm text-blue-600 hover:underline"
                                >
                                    Zaten hesabın var mı? Giriş yap
                                </button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {viewMode === 'forgot' && (
                /* ŞİFREMİ UNUTTUM EKRANI */
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="text-2xl text-center">Şifremi Unuttum</CardTitle>
                        <CardDescription className="text-center">
                            Şifrenizi sıfırlamak için kayıtlı e-posta adresinizi girin.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="forgotEmail">E-posta Adresi</Label>
                                <Input 
                                    id="forgotEmail" 
                                    type="email" 
                                    placeholder="ornek@email.com" 
                                    value={forgotEmail} 
                                    onChange={(e) => setForgotEmail(e.target.value)} 
                                    required 
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={forgotLoading}>
                                {forgotLoading ? 'Gönderiliyor...' : 'Sıfırlama Linki Gönder'}
                            </Button>
                            <div className="pt-2 text-center">
                                <button 
                                    type="button" 
                                    onClick={() => setViewMode('login')} 
                                    className="text-sm text-blue-600 hover:underline"
                                >
                                    Giriş Ekranına Dön
                                </button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}